import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";

export default function SurveyTextQuestion({ step, question, placeholder, value, onChange, onNext, onBack, isLast, submitting }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const canSubmit = value.trim().length > 0 && !submitting;

    const autoResize = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = el.scrollHeight + "px";
    }, []);

    useEffect(() => { inputRef.current?.focus(); }, []);
    useEffect(() => { autoResize(); }, [value, autoResize]);

    const handleKey = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey && canSubmit) { e.preventDefault(); onNext(); }
    }, [canSubmit, onNext]);

    return (
        <div>
            <p className="text-violet-400 font-mono text-sm mb-1">
                <span className="font-bold">{step}</span>
                <span className="text-white/30 mx-2">&rarr;</span>
                <span className="text-white/30">{step} / 4</span>
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight mb-6">{question}</h2>
            <textarea ref={inputRef} rows={3} maxLength={2000} placeholder={placeholder} value={value}
                onChange={(e) => onChange(e.target.value)} onKeyDown={handleKey}
                className="w-full bg-transparent text-white/90 text-lg placeholder:text-white/20 border-b-2 border-violet-500/60 focus:border-violet-400 outline-none resize-none pb-3 transition-colors overflow-hidden"
                style={{ caretColor: "#8b5cf6", minHeight: "4.5rem" }} />
            <div className="flex items-center gap-4 mt-8">
                <button onClick={onBack}
                    className="px-5 py-2.5 text-sm text-white/40 hover:text-white/70 transition-colors">
                    {t("survey.back")}
                </button>
                <button onClick={onNext} disabled={!canSubmit}
                    className="px-6 py-2.5 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-500 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150"
                    style={{ transform: "scale(1)", transition: "transform 160ms cubic-bezier(0.23,1,0.32,1)" }}
                    onMouseDown={(e) => { if (canSubmit) e.currentTarget.style.transform = "scale(0.97)"; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}>
                    {submitting ? t("survey.submitting") : isLast ? t("survey.submit") : t("survey.next")}
                </button>
                {canSubmit && (
                    <span className="text-xs text-white/20">{t("survey.pressEnter")}</span>
                )}
            </div>
        </div>
    );
}
